import { inject, Injectable, InjectionToken, Injector } from '@angular/core';
import { from, Observable, of, switchMap, throwError, timer } from 'rxjs';
import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
    HttpParams,
    HttpRequest,
} from '@angular/common/http';
import { mergeMap, catchError, map, tap, take, filter } from 'rxjs/operators';
import {
    SrvResponse,
    EntData,
    EntList,
    SrvSchema,
    SchemaType,
    EntSchema,
    EntTransitData,
    EntDocumentSettings,
    SrvEntData,
    GroupRequestItem,
    SrvRequestFields,
} from '@bazis/shared/models/srv.types';
import { BazisToastService } from '@bazis/shared/services/toast.service';
import { API_AUTH_USER_PATH } from '@bazis/interceptors/auth.interceptor';
import { buildFilterStr, fileDownloaderFn, getFilenameFromContentDisposition } from '@bazis/utils';
import { TranslocoService } from '@jsverse/transloco';
import { BazisWebSocketService } from '@bazis/shared/services/web-socket.service';

/**
 * **BazisSrvService**
 *
 * Low-level HTTP API client for Bazis backend.
 * Builds JSON:API requests for entities, lists, schemas and custom endpoints,
 * provides bulk/group requests support, file download helpers, and background task handling.
 *
 * This service is used by higher-level services like `BazisEntityService`, auth, documents, etc.
 */
export const HEADERS = new HttpHeaders()
    .set('Content-Type', 'application/vnd.api+json')
    .set('Accept', 'application/vnd.api+json');

/**
 * Injection token for base API URL.
 *
 * Default: `/api/web/v1`.
 *
 * Override in providers when you deploy API under a different base path:
 *
 * ```ts
 * providers: [{ provide: API_URL, useValue: 'https://example.com/api/web/v1' }]
 * ```
 */
export const API_URL = new InjectionToken<string>('API_URL', {
    providedIn: 'root',
    factory: () => '/api/web/v1',
});

/**
 * Injection token for backend API version string.
 *
 * Default: `'1'`.
 *
 * Some parts of schema/meta processing depend on API version.
 *
 * ```ts
 * providers: [{ provide: BAZIS_API_V, useValue: '2' }]
 * ```
 */
export const BAZIS_API_V = new InjectionToken<string>('BAZIS_API_V', {
    providedIn: 'root',
    factory: () => '1',
});

@Injectable({
    providedIn: 'root',
})
export class BazisSrvService {
    /** Angular `HttpClient` used to execute requests. */
    protected http = inject(HttpClient);

    /** Toast service used for API error messages and file receive errors. */
    protected toast = inject(BazisToastService);

    /** Transloco service used to add `lang` parameter to some requests. */
    protected translocoService = inject(TranslocoService);

    /** Injector used for late/lazy dependency lookup (example: websocket service). */
    protected injector = inject(Injector);

    /** Base API URL string resolved from `API_URL` token. */
    readonly apiUrl = inject(API_URL, { optional: true });

    /** Backend API version resolved from `BAZIS_API_V` token. */
    readonly bazisApiV = inject(BAZIS_API_V, { optional: true });

    /**
     * Fetch a single entity using JSON:API.
     *
     * @param entityType - Entity type string.
     * @param entid - Entity id string.
     * @param include - Include list for related resources. Default: [].
     * @param fields - Optional sparse fieldsets map. Default: null.
     * @param skipErrorToast - If true, do not show error toast. Default: false.
     * @param isBackgroundRequest - If true, send as background task request. Default: false.
     * @returns Observable of entity `EntData`.
     */
    fetchEntity$(
        entityType: string,
        entid: string,
        include: string[] = [],
        fields: SrvRequestFields = null,
        skipErrorToast: boolean = false,
        isBackgroundRequest: boolean = false,
    ): Observable<EntData> {
        return this.commonEntRequest$<EntData>('get', {
            entityType,
            entid,
            include,
            fields,
            skipErrorToast,
            isBackgroundRequest,
        });
    }

    /**
     * Create an entity (POST).
     *
     * @param entityType - Entity type string.
     * @param srvData - Request body (JSON:API format).
     * @param include - Include list. Default: [].
     * @param fields - Optional sparse fieldsets map. Default: null.
     * @param isBackgroundRequest - If true, send as background task request. Default: false.
     * @returns Observable of created entity `EntData`.
     */
    createEntity$(
        entityType,
        srvData,
        include = [],
        fields: SrvRequestFields = null,
        isBackgroundRequest = false,
    ): Observable<EntData> {
        return this.commonEntRequest$<EntData>('post', {
            entityType,
            data: srvData,
            include,
            fields,
            isBackgroundRequest,
        });
    }

    /**
     * Save/update an entity (PATCH).
     *
     * @param entityType - Entity type string.
     * @param entid - Entity id string.
     * @param srvData - Request body (JSON:API format).
     * @param include - Include list. Default: [].
     * @param fields - Optional sparse fieldsets map. Default: null.
     * @param isBackgroundRequest - If true, send as background task request. Default: false.
     * @returns Observable of updated entity `EntData`.
     */
    saveEntity$(
        entityType,
        entid,
        srvData,
        include = [],
        fields: SrvRequestFields = null,
        isBackgroundRequest = false,
    ): Observable<EntData> {
        return this.commonEntRequest$<EntData>('patch', {
            entityType,
            entid,
            data: srvData,
            include,
            fields,
            isBackgroundRequest,
        });
    }

    /**
     * Delete an entity (DELETE).
     *
     * @param entityType - Entity type string.
     * @param entid - Entity id string.
     * @param isBackgroundRequest - If true, send as background task request. Default: false.
     * @returns Observable of backend response as `EntData` (may be empty).
     */
    deleteEntity$(entityType, entid, isBackgroundRequest = false): Observable<EntData> {
        return this.commonEntRequest$<EntData>('delete', {
            entityType,
            entid,
            isBackgroundRequest,
        });
    }

    /**
     * Cancel previous document signings for an entity.
     *
     * @param entityType - Entity type string.
     * @param entid - Entity id string.
     * @param contextLabel - Optional signing context label. Default: null.
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of response entity.
     */
    cancelEntityDocumentSignings$(
        entityType,
        entid,
        contextLabel = null,
        isBackgroundRequest = false,
    ): Observable<EntData> {
        return this.commonEntRequest$<EntData>('post', {
            entityType,
            entid,
            suffix: 'document_signing_cancel' + (contextLabel ? '/' + contextLabel : ''),
            isBackgroundRequest,
        });
    }

    /**
     * Request a signable document for an entity.
     *
     * @param entityType - Entity type string.
     * @param entid - Entity id string.
     * @param contextLabel - Optional signing context label. Default: null.
     * @param data - Optional request payload. Default: {}.
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of document data as `EntData`.
     */
    fetchEntityDocument$(
        entityType,
        entid,
        contextLabel = null,
        data = {},
        isBackgroundRequest = false,
    ): Observable<EntData> {
        return this.commonEntRequest$<EntData>('post', {
            entityType,
            entid,
            suffix: 'document_signing' + (contextLabel ? '/' + contextLabel : ''),
            data,
            isBackgroundRequest,
        });
    }

    /**
     * Add or remove priority for an entity.
     *
     * Uses POST when `value` is truthy, otherwise DELETE.
     *
     * @param entityType - Entity type string.
     * @param entid - Entity id string.
     * @param value - Priority value (truthy to add, falsy to delete).
     * @param priority - Priority id/key sent as request param.
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of response entity.
     */
    setPriority$(
        entityType,
        entid,
        value,
        priority,
        isBackgroundRequest = false,
    ): Observable<EntData> {
        return this.commonEntRequest$<EntData>(value ? 'post' : 'delete', {
            entityType,
            entid,
            suffix: value ? 'add_priority' : 'delete_priority',
            params: { priority },
            isBackgroundRequest,
        });
    }

    /**
     * Fetch full list of entities (up to 1000 items).
     *
     * @param entityType - Entity type string.
     * @param suffix - Optional endpoint suffix. Default: empty string.
     * @param params - Optional query params. Default: null.
     * @param fields - Optional sparse fieldsets map. Default: null.
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of list (`EntList`).
     */
    fetchAllEntities$(
        entityType,
        suffix = '',
        params = null,
        fields: SrvRequestFields = null,
        isBackgroundRequest = false,
    ): Observable<EntList> {
        return this.commonEntRequest$<EntList>('get', {
            entityType,
            params,
            suffix,
            fields,
            limit: 1000,
            offset: 0,
            isBackgroundRequest,
        });
    }

    /**
     * Fetch a list portion with pagination/search and optional meta/include.
     *
     * @param entityType - Entity type string.
     * @param suffix - Optional endpoint suffix.
     * @param offset - Pagination offset.
     * @param limit - Page size.
     * @param search - Search string.
     * @param params - Additional query params.
     * @param meta - Meta fields list. Default: [].
     * @param fields - Optional sparse fieldsets map. Default: null.
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of list (`EntList`).
     */
    fetchPortion$(
        entityType,
        suffix,
        offset,
        limit,
        search,
        params,
        meta = [],
        fields: SrvRequestFields = null,
        isBackgroundRequest = false,
    ): Observable<EntList> {
        return this.commonEntRequest$<EntList>('get', {
            entityType,
            suffix,
            offset,
            limit,
            search,
            params,
            meta,
            fields,
            isBackgroundRequest,
        });
    }

    /**
     * Fetch and transform schema from backend.
     *
     * Builds URL: `<apiUrl>/<entityType>/<id>/<schemaType>/` and converts backend schema
     * into front-end `EntSchema` format with `attributes`, `relationships`, `required`, and `included`.
     *
     * @param schemaType - Schema type (example: `schema_create`, `schema_update`).
     * @param entityType - Entity type string.
     * @param entid - Optional entity id. Default: empty string.
     * @param include - Optional include list. Default: [].
     * @returns Observable of schema (`EntSchema`) or error response.
     */
    fetchSchema$(
        schemaType: SchemaType,
        entityType: string,
        entid: string = '',
        include: string[] = [],
    ): Observable<EntSchema> {
        let endpointKey = entityType.split('.').join('/');

        const urlApi = this.apiUrl;

        let url = entid ? `${urlApi}/${endpointKey}/${entid}/` : `${urlApi}/${endpointKey}/`;
        url += `${schemaType}/`;
        const paramsObj: any = {};
        if (include) {
            paramsObj.include = include.join(',');
        }
        const params = new HttpParams({ fromObject: paramsObj });
        return this.http.get<SrvSchema>(url, { headers: HEADERS, params }).pipe(
            map((response: SrvSchema) => {
                // console.log('[SRV][RESPONSE]:', response);
                if (response.error === 403) {
                    // console.log('[SRV][RESPONSE][ERROR]:', response);
                    return response;
                }
                const definitions = response.definitions || response.$defs;
                const getKey = (key) => key.split('/').slice(-1)[0];
                const getDefinitionKey = (property) => {
                    // TODO: multiple allFor
                    return property.$ref
                        ? getKey(property.$ref)
                        : property.allOf
                          ? getKey(property.allOf[0].$ref)
                          : getKey(property.anyOf[0].$ref);
                };
                const generateRelationships = (properties) => {
                    const relationships: any = {};
                    for (let key in properties) {
                        relationships[key] = {
                            ...properties[key],
                        };
                        let definitionKey = getDefinitionKey(properties[key]);
                        delete relationships[key].allOf;
                        delete relationships[key].anyOf;
                        let propertyObject = definitions[definitionKey].properties;
                        if (propertyObject.data.anyOf) {
                            if (propertyObject?.data.anyOf[0].type === 'array') {
                                definitionKey = getDefinitionKey(
                                    propertyObject?.data.anyOf[0].items,
                                );
                                relationships[key].type = 'array';
                            } else {
                                definitionKey = getDefinitionKey(propertyObject?.data.anyOf[0]);
                                relationships[key].type = 'object';
                            }
                        } else {
                            if (propertyObject?.data?.type === 'array') {
                                definitionKey = getDefinitionKey(propertyObject.data.items);
                                relationships[key].type = 'array';
                            } else {
                                definitionKey = getDefinitionKey(propertyObject.data);
                                relationships[key].type = 'object';
                            }
                        }
                        propertyObject = definitions[definitionKey].properties;
                        relationships[key].entityType = propertyObject.type.default;
                    }
                    return relationships;
                };

                const actionToschemaTypeMap = {
                    add: 'schema_create',
                    change: 'schema_update',
                    transit: 'schema_transit',
                    view: 'schema_retrieve',
                };

                const actionProperty = this.bazisApiV === '1' ? 'action' : 'bs:action';

                const generateSchema = (properties, schema) => {
                    properties.forEach((allOfItem) => {
                        const rootKey = getKey(allOfItem.$ref);
                        if (definitions[rootKey].properties.attributes.$ref) {
                            definitions[rootKey].properties.attributes.allOf = [
                                definitions[rootKey].properties.attributes,
                            ];
                        }

                        if (!schema.id && definitions[rootKey].properties.id?.default) {
                            schema.id = definitions[rootKey].properties.id?.default;
                        }
                        if (!schema.entityType && definitions[rootKey].properties.type?.default) {
                            schema.entityType = definitions[rootKey].properties.type?.default;
                        }
                        if (
                            !schema.schemaType &&
                            definitions[rootKey].properties[actionProperty]?.default
                        ) {
                            schema.schemaType =
                                actionToschemaTypeMap[
                                    definitions[rootKey].properties[actionProperty].default
                                ];
                        }

                        definitions[rootKey].properties.attributes.allOf.forEach(
                            (allOfAttribute) => {
                                const attributesKey = getKey(allOfAttribute.$ref);

                                schema.attributes = {
                                    ...schema.attributes,
                                    ...definitions[attributesKey].properties,
                                };
                                schema.required = [
                                    ...schema.required,
                                    ...(definitions[attributesKey].required || []),
                                ];
                            },
                        );

                        if (definitions[rootKey].properties.relationships?.$ref) {
                            definitions[rootKey].properties.relationships.allOf = [
                                definitions[rootKey].properties.relationships,
                            ];
                        }
                        if (definitions[rootKey].properties.relationships?.anyOf) {
                            definitions[rootKey].properties.relationships.allOf =
                                definitions[rootKey].properties.relationships?.anyOf;
                        }

                        definitions[rootKey].properties.relationships?.allOf?.forEach(
                            (allOfRelationship) => {
                                if (!allOfRelationship.$ref) return;
                                const relationshipsKey = getKey(allOfRelationship.$ref);
                                schema.required = [
                                    ...schema.required,
                                    ...(definitions[relationshipsKey].required || []),
                                ];
                                schema.relationships = {
                                    ...schema.relationships,
                                    ...generateRelationships(
                                        definitions[relationshipsKey].properties,
                                    ),
                                };
                            },
                        );
                    });
                    return schema;
                };

                let schema: any = {
                    entityType: entityType,
                    id: entid,
                    attributes: {},
                    relationships: {},
                    schemaType: schemaType,
                    required: [],
                    included: [],
                };
                if (response.properties.data.$ref) {
                    response.properties.data.allOf = [response.properties.data];
                }
                const properties = response.properties.data.allOf || [
                    response.properties.data.items,
                ];
                schema = generateSchema(properties, schema);
                if (response.properties.included) {
                    let includedItems =
                        response.properties.included.items ||
                        response.properties.included.anyOf[0].items;
                    includedItems = includedItems.anyOf ? includedItems.anyOf : [includedItems];
                    includedItems.forEach((includedItem) => {
                        schema.included.push(
                            generateSchema([includedItem], {
                                attributes: {},
                                relationships: {},
                                required: [],
                            }),
                        );
                    });
                }
                // console.log('schema in front format', schema);
                return schema;
            }),
        );
    }

    /**
     * Execute a JSON:API entity/list request and transform response to `EntData` / `EntList` format.
     *
     * Adds meta fields and included entities when present.
     * Handles error toasts (unless `skipErrorToast` is true) and rethrows the error.
     *
     * @typeParam T - Expected transformed result type (usually `EntData` or `EntList`).
     * @param method - HTTP method (`post`, `get`, `delete`, `put`, `patch`).
     * @param o - Request settings (entity type, id, params, meta, include, etc.).
     * @returns Observable of transformed result.
     */
    protected commonEntRequest$<T = EntData>(
        method: 'post' | 'get' | 'delete' | 'put' | 'patch',
        o: {
            entityType: string;
            entid?: any;
            data?: any;
            limit?: number;
            offset?: number;
            search?: string;
            params?: { [key: string]: string | string[] };
            urlApi?: string;
            suffix?: string;
            meta?: string[];
            include?: string[];
            skipErrorToast?: boolean;
            isBackgroundRequest?: boolean;
            fields?: SrvRequestFields;
        },
    ): Observable<T> {
        let url = this.generateBasicUrl(o.entityType, o.entid, o.suffix, o.urlApi);

        let meta = o.meta || [];
        // если есть намек на лист, то нужна в мета pagination
        if (o.offset !== undefined || o.limit !== undefined) {
            meta.push('pagination');
        }

        // если получаем сущность не классификатор, то нужен в мета state_actions
        if (
            o.entityType &&
            (o.entid || method === 'post') &&
            o.entityType.indexOf('classifier.') === -1
        ) {
            meta.push('state_actions');
            meta.push('crud_actions');
        }

        const paramsObj = o.params || {};
        if (o.offset !== undefined) {
            paramsObj['page[offset]'] = String(o.offset);
        }
        if (o.limit !== undefined) {
            paramsObj['page[limit]'] = String(o.limit);
        }
        if (o.fields) {
            Object.keys(o.fields).forEach((key) => {
                paramsObj[`fields[${key}]`] = o.fields[key].join(',');
            });
        }
        if (o.search !== undefined) {
            paramsObj.search = String(o.search);
        }
        if (meta.length > 0) {
            paramsObj.meta = Array.from(new Set(meta)).join(',');
        }
        if (o.include && o.include.length > 0) {
            paramsObj.include = o.include.join(',');
        }

        return this.executeRequest$({
            method,
            url,
            body: o.data,
            params: paramsObj,
            isBackgroundRequest: o.isBackgroundRequest,
        }).pipe(
            map((response: SrvResponse) => {
                //console.log('[SRV][RESPONSE]:', response, '[WITH]:', o);
                if (response?.data) {
                    let result: any | any[] = response.data;
                    result =
                        result instanceof Array
                            ? this.refineList(response, o)
                            : this.makeupEntity(response.data as SrvEntData);

                    result = this.addMetaInfo(result, response.meta);
                    if (response.included && response.included.map) {
                        result.included = response.included.map((includedItem) =>
                            this.makeupEntity(includedItem),
                        );
                    }
                    return result;
                } else return response;
            }),
            catchError((e) => {
                if (
                    (e?.error?.url && e.error.url.indexOf(API_AUTH_USER_PATH) > -1) ||
                    e.status === 409
                ) {
                    return throwError(e);
                }
                console.log('[SRV][ERROR]', e);
                if (!o.skipErrorToast) {
                    let message = this.generateErrorMessage(e);
                    this.toast.create({
                        titleKey: 'toast.apiError.title',
                        messageKey: 'toast.apiError.message',
                        messageParams: { message },
                        type: 'error',
                    });
                }
                return throwError(e);
            }),
        );
    }

    /**
     * Convert JSON:API list response to `EntList` shape.
     *
     * @param response - Raw server response that contains `data` array.
     * @param o - Request options object (used for `entityType`).
     * @returns `EntList` object with `list` of `EntData` items.
     */
    protected refineList(response, o): EntList {
        const data = response.data;
        let result = {
            type: o.entityType,
            list: data.map((ent) => this.makeupEntity(ent)),
        };
        return result;
    }

    /**
     * Convert JSON:API list response to `EntList` shape.
     *
     * @param response - Raw server response that contains `data` array.
     * @param o - Request options object (used for `entityType`).
     * @returns `EntList` object with `list` of `EntData` items.
     */
    protected addMetaInfo(result, meta) {
        if (!meta) {
            return result;
        }
        result.$meta = meta;
        if (meta.state_actions) {
            const actionSigning = meta.state_actions.filter(
                (action) => action.code === 'ACTION_DOCUMENT_SIGNING' && !action.restricts,
            );
            const actionSign = meta.state_actions.filter(
                (action) => action.code === 'ACTION_SIGN' && !action.restricts,
            );

            const signActions = actionSign.concat(actionSigning);
            result.$canSign = signActions.length > 0;
            if (result.$canSign) {
                const signContexts = Array.from(
                    new Set(
                        signActions
                            .map((action) => {
                                const urlParts = action.endpoint.url.split('/');
                                const contextPart = urlParts[urlParts.length - 2];
                                return contextPart !== 'sign' ? contextPart : null;
                            })
                            .filter(Boolean),
                    ),
                );

                result.$signContext = signContexts.length > 1 ? signContexts : signContexts[0];
            }

            const actionTransits = meta.state_actions.filter(
                (action) =>
                    action.code === 'ACTION_TRANSIT' ||
                    (Array.isArray(action) &&
                        action.filter((v) => v.code === 'ACTION_TRANSIT').length === action.length),
            );

            result.$transits = actionTransits
                .map((current) =>
                    Array.isArray(current)
                        ? current.map((v) => v.endpoint?.body.properties?.transit?.default)
                        : [current.endpoint?.body.properties?.transit?.default],
                )
                .filter((v) => !!v);

            result.$transitMap = actionTransits
                .reduce((acc, current) => {
                    if (Array.isArray(current)) {
                        acc = acc.concat(current);
                    } else {
                        acc.push(current);
                    }
                    return acc;
                }, [])
                .reduce((acc, current) => {
                    const transit = current.endpoint.body.properties;
                    const criticalRestricts =
                        current.restricts?.filter(
                            (v) =>
                                v.code !== 'USER_SIGNATURE_REQUIRED' &&
                                v.code !== 'TRANSIT_RELATED_REQUIRED',
                        ) || [];
                    const uniqueRestrictTextsToDisplay = Array.from(
                        new Set(criticalRestricts.map((v) => v.detail)),
                    );
                    const needMySign =
                        current.restricts?.filter((v) => v.code === 'USER_SIGNATURE_REQUIRED')
                            .length > 0;
                    const needOtherSign =
                        current.restricts?.filter((v) => v.code === 'SIGNATURE_REQUIRED').length >
                        0;

                    acc[transit.transit.default] = {
                        id: transit.transit.default,
                        url: current.endpoint.url,
                        isAllowed: !criticalRestricts.length,
                        restricts: current.restricts,
                        hint: current.hint,
                        needMySign,
                        needOtherSign,
                        showSignButtonInsteadOfTransit: needMySign && needOtherSign,
                        restrictsToDisplay: criticalRestricts,
                        uniqueRestrictTextsToDisplay,
                        ...this.makeupTransitPayload(
                            current.endpoint.body.properties,
                            current.endpoint.body.definitions || current.endpoint.body.$defs,
                        ),
                    };
                    return acc;
                }, {});

            result.$transits.forEach((group) => {
                if (group.length > 1) {
                    group.forEach((groupItem, index) => {
                        if (index > 0) {
                            result.$transitMap[group[0]].restrictsToDisplay = result.$transitMap[
                                group[0]
                            ].restrictsToDisplay.concat(
                                result.$transitMap[groupItem].restrictsToDisplay,
                            );
                        }
                    });
                    result.$transitMap[group[0]].isAllowed =
                        !result.$transitMap[group[0]].restrictsToDisplay.length;
                }
            });
        }
        if (meta.for_change) {
            result.list = result.list.map((entity) => ({
                ...entity,
                $canChange: meta.for_change.indexOf(entity.id) > -1,
                $canView: true,
            }));
        }
        if (meta.for_delete) {
            result.list = result.list.map((entity) => ({
                ...entity,
                $canDelete: meta.for_delete.indexOf(entity.id) > -1,
            }));
        }
        if (meta.for_create) {
            result.$canAdd = meta.for_create;
        }
        if (meta.crud_actions) {
            const crudMap = {
                view: '$canView',
                add: '$canAdd',
                delete: '$canDelete',
                change: '$canChange',
            };
            Object.keys(crudMap).forEach((crudAction) => {
                result[crudMap[crudAction]] = meta.crud_actions.indexOf(crudAction) > -1;
            });
        }
        //console.log(result);
        return result;
    }

    /**
     * Attach meta info and derived client helpers to entity/list results.
     *
     * Adds `$meta` and derived fields like:
     * - `$canSign`, `$signContext`
     * - `$transits`, `$transitMap`
     * - `$canView`, `$canAdd`, `$canDelete`, `$canChange`
     *
     * @param result - Entity or list result to enrich.
     * @param meta - Meta object from backend response.
     * @returns Enriched result (same object reference).
     */

    /**
     * Build transit payload UI schema map from backend schema definitions.
     *
     * This helper is used when backend provides transit payload schema in meta actions.
     * It produces a nested map of fields with component hints (input/options/file) and required flags.
     *
     * @param properties - Schema properties object from backend action body.
     * @param definitions - Schema definitions map (`definitions` or `$defs`).
     * @returns Object with `payload` map or `null` when payload is not required.
     */
    makeupTransitPayload(properties, definitions): { payload: any } {
        if (!properties || !definitions) return { payload: null };

        let payload = [];
        let payloadMap = {};

        const addToMap = (initialRef, mapToAdd: any) => {
            const refArray = initialRef.split('/');
            const ref = refArray[refArray.length - 1];
            const payloadProperties = definitions[ref].properties;
            const requiredFields = definitions[ref].required || [];

            Object.keys(payloadProperties).forEach((property) => {
                if (
                    payloadProperties[property].$ref ||
                    (payloadProperties[property].anyOf &&
                        payloadProperties[property].anyOf.find((v) => v.$ref))
                ) {
                    const calcRef =
                        payloadProperties[property].$ref ||
                        payloadProperties[property].anyOf.find((v) => v.$ref).$ref;
                    mapToAdd[property] = {};
                    addToMap(calcRef, mapToAdd[property]);
                    return;
                }
                let component = 'input';
                if (payloadProperties[property].extentions) {
                    component = 'file';
                } else if (payloadProperties[property].enum) {
                    component = 'options';
                }

                payload.push(property);
                mapToAdd[property] = {
                    ...payloadProperties[property],
                    required: requiredFields.indexOf(property) > -1,
                    component,
                };

                if (payloadProperties[property].enumDict) {
                    const optionList = [];
                    Object.keys(payloadProperties[property].enumDict).forEach((enumItem) => {
                        optionList.push({
                            id: enumItem,
                            name: payloadProperties[property].enumDict[enumItem],
                        });
                    });
                    mapToAdd[property].optionList = optionList;
                    mapToAdd[property].isMultiple = mapToAdd[property].type !== 'string';
                }
            });
        };

        if (properties.payload?.$ref) {
            addToMap(properties.payload.$ref, payloadMap);
        } else if (properties) {
            Object.keys(properties).forEach((propertyKey) => {
                if (properties[propertyKey].$ref) {
                    payloadMap = { ...payloadMap, [propertyKey]: {} };
                    addToMap(properties[propertyKey].$ref, payloadMap[propertyKey]);
                }
            });
        }

        return {
            payload: payload.length > 0 ? payloadMap : null,
        };
    }

    /**
     * Convert server entity data (`SrvEntData`) to client entity format (`EntData`).
     *
     * Copies attributes into `$snapshot` and flattens relationships into `$snapshot[prop] = data`.
     *
     * @param ent - Server entity object in JSON:API format.
     * @returns Client entity object (`EntData`).
     */
    makeupEntity(ent: SrvEntData): EntData {
        let entity: EntData = {
            type: ent.type,
            id: ent.id,
            $snapshot: {
                id: ent.id,
                type: ent.type,
                ...ent.attributes,
            },
        };

        if (ent.relationships) {
            Object.keys(ent.relationships).forEach((propKey) => {
                entity.$snapshot[propKey] = ent.relationships[propKey].data;
            });
        }
        return entity;
    }

    /**
     * Sign one entity document using direct sign endpoint.
     *
     * @param settings - Document signing settings (entity type/id and optional contextLabel).
     * @param signature - Signature value to send to backend.
     * @param signBodyPayload - Optional additional payload fields. Default: null.
     * @param urlApi - Optional API base URL override. Default: empty string (uses `apiUrl`).
     * @returns Observable of server response.
     */
    signEntity$(settings: EntDocumentSettings, signature, signBodyPayload = null, urlApi = '') {
        let url = this.generateBasicUrl(
            settings.entityType,
            settings.entityId,
            'sign' + (settings.contextLabel ? '/' + settings.contextLabel : ''),
            urlApi,
        );
        const signatureParams = { ...signBodyPayload, signature };
        return this.http.post<SrvResponse>(url, signatureParams, { headers: HEADERS }).pipe(
            catchError((e) => {
                console.log('[SRV][ERROR]', e);
                return throwError(e);
            }),
        );
    }

    /**
     * Sign multiple entity documents using bulk endpoint.
     *
     * Builds bulk endpoints list and posts to `<apiUrl>/bulk/`.
     * On error, shows error toast and rethrows.
     *
     * @param signSettings - Array of signing settings objects.
     * @param urlApi - Optional API base URL override. Default: empty string (uses `apiUrl`).
     * @returns Observable of backend bulk response.
     */
    signEntities$(
        signSettings: { settings: EntDocumentSettings; signature; signBodyPayload }[],
        urlApi = '',
    ) {
        const endpoints = signSettings.map((setting) => {
            return {
                method: 'POST',
                endpoint: this.generateBasicUrl(
                    setting.settings.entityType,
                    setting.settings.entityId,
                    'sign' +
                        (setting.settings.contextLabel ? '/' + setting.settings.contextLabel : ''),
                    urlApi,
                    true,
                ),
                body: {
                    ...setting.signBodyPayload,
                    signature: setting.signature,
                },
            };
        });
        return this.http.post(`${this.apiUrl}/bulk/`, endpoints).pipe(
            catchError((error) => {
                this.toast.create({
                    titleKey: 'toast.apiError.title',
                    message: this.generateErrorMessage(error),
                    type: 'error',
                });
                throw error;
            }),
        );
    }

    /**
     * Perform one or multiple transit operations via backend transit endpoint.
     *
     * For a single item, sends a direct POST request and returns transformed `EntData` with meta.
     * For multiple items, sends a bulk request to `<apiUrl>/bulk/` and processes each response.
     *
     * @param settings - Array of transit settings (either `{ entityType, entityId }` or explicit `url`).
     * @param isBackgroundRequest - If true, uses background task execution. Default: false.
     * @returns Observable of transit result (`EntData` for single, array for bulk).
     */
    transitEntity$(
        settings: {
            entityType?: string;
            entityId?: string;
            url?: string;
            transitParams: EntTransitData;
            urlApi?: string;
        }[],
        isBackgroundRequest: boolean = false,
    ): Observable<EntData | EntData[]> {
        const generateTransitUrl = (params, addLangParam = false) => {
            if (!params.url && params.entityType && params.entityId) {
                return this.generateBasicUrl(
                    params.entityType,
                    params.entityId,
                    'transit',
                    params.urlApi,
                    addLangParam,
                );
            }
            if (!addLangParam) return params.url;

            const urlParts = params.url.split('?');
            const getParts = urlParts.length > 1 ? urlParts[1].split('&') : [];
            getParts.push(this._getLangParam());
            return `${urlParts[0]}?${getParts.join('&')}`;
        };

        const meta = 'state_actions,crud_actions';

        if (settings.length === 1) {
            return this.executeRequest$<SrvResponse>({
                method: 'post',
                url: generateTransitUrl(settings[0]),
                params: {
                    meta,
                },
                body: settings[0].transitParams,
                isBackgroundRequest,
            }).pipe(
                map((response) => {
                    if (response?.data) {
                        let result: any = response.data;
                        if (!result?.id) return result;

                        result = this.makeupEntity(response.data as SrvEntData);
                        result = this.addMetaInfo(result, response.meta);
                        return result;
                    } else return response;
                }),
            );
        }

        const endpoints = settings.map((setting) => {
            return {
                method: 'POST',
                endpoint: generateTransitUrl(setting, true) + `&meta=${meta}`,
                body: {
                    ...setting.transitParams,
                },
            };
        });

        return this.executeRequest$({
            method: 'post',
            url: `${this.apiUrl}/bulk/`,
            body: endpoints,
            isBackgroundRequest,
        }).pipe(map((responses: any) => this.processBulkResponse(responses)));
    }

    /**
     * Build a basic JSON:API URL for an entity endpoint.
     *
     * Converts `entityType` from dotted format to path format (`foo.bar` -> `foo/bar`),
     * appends optional `entityId` and optional `suffix`.
     *
     * @param entityType - Entity type string.
     * @param entityId - Optional entity id. Default: empty string.
     * @param suffix - Optional suffix appended after entity id (example: `transit`). Default: empty string.
     * @param urlApi - Optional API base URL override. Default: empty string (uses `apiUrl`).
     * @param addLangParam - If true, appends `?lang=<activeLang>`. Default: false.
     * @returns URL string.
     */
    generateBasicUrl(
        entityType: string,
        entityId: string = '',
        suffix: string = '',
        urlApi = '',
        addLangParam = false,
    ): string {
        const endpointKey = entityType.split('.').join('/');
        const suffixKey = suffix ? suffix + '/' : '';
        urlApi = urlApi || this.apiUrl;
        let url = entityId
            ? `${urlApi}/${endpointKey}/${entityId}/${suffixKey}`
            : `${urlApi}/${endpointKey}/${suffixKey}`;
        if (addLangParam) url += `?${this._getLangParam()}`;
        return url;
    }

    // for requests with not typical content-type format
    /**
     * Send a request to a non-JSON:API endpoint using JSON or form-encoded payload.
     *
     * Delegates to `commonFormRequest$()`.
     *
     * @typeParam T - Expected response type.
     * @param endpoint - Endpoint path relative to `apiUrl` (without trailing slash).
     * @param params - Request body payload. Default: {}.
     * @param processResponseAsEntity - If true, transforms JSON:API response to `EntData`. Default: false.
     * @param method - HTTP method (`post`, `put`, `patch`). Default: `post`.
     * @param getParams - Optional query params object. Default: null.
     * @param header - Content-Type header. Default: `application/json`.
     * @param urlApi - Optional API base URL override. Default: empty string (uses `apiUrl`).
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of response.
     */
    sendFormRequest$<T = any>(
        endpoint: string,
        params: any = {},
        processResponseAsEntity: boolean = false,
        method: 'post' | 'put' | 'patch' = 'post',
        getParams = null,
        header: string = 'application/json',
        urlApi: string = '',
        isBackgroundRequest: boolean = false,
    ): Observable<T> {
        return this.commonFormRequest$(method, {
            endpoint,
            params,
            getParams,
            header,
            urlApi,
            processResponseAsEntity,
            isBackgroundRequest,
        });
    }

    /**
     * Download a file as Blob and trigger browser download.
     *
     * Supports POST and GET. Can optionally extract filename from `Content-Disposition` header.
     *
     * On error, tries to parse error body as text -> JSON and shows error toast.
     *
     * @param endpoint - Endpoint path relative to `apiUrl`.
     * @param fileName - Fallback filename for downloaded file.
     * @param params - POST body params. Default: {}.
     * @param getParams - Query params. Default: {}.
     * @param method - HTTP method (`post` or `get`). Default: `post`.
     * @param urlApi - Optional API base URL override. Default: empty string.
     * @param filenameFromResponseHeaders - If true, reads filename from response headers. Default: false.
     * @returns Observable that emits downloaded Blob.
     */
    downloadFile$(
        endpoint: string,
        fileName: string,
        params = {},
        getParams = {},
        method: 'post' | 'get' = 'post',
        urlApi: string = '',
        filenameFromResponseHeaders: boolean = false,
    ) {
        let url = `${urlApi || this.apiUrl}/${endpoint}/`;
        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
            responseType: 'blob' as 'json',
            observe: 'response' as 'response',
            params: new HttpParams({ fromObject: getParams || {} }),
        };
        const observable$ =
            method === 'post' ? this.http.post(url, params, options) : this.http.get(url, options);
        return observable$.pipe(
            map((response: any) => {
                const blob = response.body as Blob;
                let _fileName = fileName;

                if (filenameFromResponseHeaders) {
                    const cd =
                        response.headers.get('Content-Disposition') ||
                        response.headers.get('content-disposition');

                    _fileName = getFilenameFromContentDisposition(cd) || fileName;
                }

                fileDownloaderFn(window.URL.createObjectURL(blob), _fileName);

                return blob;
            }),
            catchError((error) => {
                return from(error.error.text()).pipe(
                    switchMap((errorText: string) => {
                        const formattedError = {
                            ...error,
                            error: JSON.parse(errorText),
                        };
                        this.toast.create({
                            titleKey: 'toast.fileReceiveError.title',
                            messageKey: 'toast.fileReceiveError.message',
                            messageParams: {
                                message: this.generateErrorMessage(formattedError),
                            },
                            type: 'error',
                        });
                        return throwError(formattedError);
                    }),
                );
            }),
        );
    }

    getFileData$(endpoint: string, urlApi: string = '') {
        let url = `${urlApi || this.apiUrl}/${endpoint}/`;

        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
            responseType: 'blob' as 'json',
        };

        return this.http.get(url, options).pipe(
            catchError((error) => {
                this.toast.create({
                    titleKey: 'toast.fileReceiveError.title',
                    messageKey: 'toast.fileReceiveError.message',
                    messageParams: {
                        message: this.generateErrorMessage(error),
                    },
                    type: 'error',
                });
                return of(error);
            }),
        );
    }

    /**
     * Send a form-style request (non JSON:API entity endpoint).
     *
     * Supports JSON payload and `application/x-www-form-urlencoded` payload (when header is set).
     * Can optionally transform response to `EntData` when `processResponseAsEntity` is true.
     *
     * @param method - HTTP method (`post`, `put`, `patch`).
     * @param o - Request settings.
     * @returns Observable of raw response or transformed `EntData`.
     */
    protected commonFormRequest$(
        method: 'post' | 'put' | 'patch',
        o: {
            endpoint: string;
            params?: any;
            getParams?: any;
            urlApi?: string;
            header: string;
            processResponseAsEntity: boolean;
            isBackgroundRequest?: boolean;
        },
    ) {
        const urlApi = o.urlApi || this.apiUrl;
        let url = `${urlApi}/${o.endpoint}/`;

        const options: { headers: HttpHeaders; params?: any } = {
            headers: new HttpHeaders().set(
                'Content-Type',
                o.header || 'application/x-www-form-urlencoded',
            ),
        };
        if (o.getParams) {
            options.params = new HttpParams({ fromObject: o.getParams || {} });
        }

        if (o.params instanceof Object && o.header === 'application/x-www-form-urlencoded') {
            let body = new URLSearchParams();
            for (let key in o.params) {
                body.set(key, o.params[key]);
            }
            return this.http[method](url, body, options);
        }

        return this.http[method](url, o.params, options).pipe(
            map((response: any) => {
                if (!o.processResponseAsEntity) return response;

                let result = this.makeupEntity(response.data as SrvEntData);
                result = this.addMetaInfo(result, response.meta);
                return result;
            }),
        );
    }

    /**
     * Execute a GET request to a non-entity endpoint.
     *
     * @typeParam T - Expected response type.
     * @param endpoint - Endpoint path relative to `apiUrl`.
     * @param params - Optional query params object. Default: null.
     * @param urlApi - Optional API base URL override. Default: empty string.
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of response.
     */
    commonGetRequest$<T = any>(
        endpoint,
        params: any = null,
        urlApi = '',
        isBackgroundRequest = false,
    ) {
        let url = `${urlApi || this.apiUrl}/${endpoint}/`;
        return this.executeRequest$<T>({
            method: 'get',
            url,
            params,
            isBackgroundRequest,
        });
    }

    /**
     * Request counts for multiple list filters.
     *
     * Builds absolute URLs with encoded query params for each setting and sends them to
     * `analytic.count` endpoint. Returns original settings with filled `count` field.
     *
     * @param settings - Array of count settings (entityType/url + filters/params).
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of settings array with `count` numbers.
     */
    countRequest$(
        settings: {
            entityType?: string;
            filters?: any;
            filterParams?: any;
            params?: any;
            url?: string;
            count?: number;
        }[],
        isBackgroundRequest = false,
    ) {
        settings = settings.map((setting) => {
            let endpoint = setting.entityType
                ? this.generateBasicUrl(setting.entityType, '', '', this.apiUrl)
                : this.apiUrl + setting.url;

            const paramsObj: any =
                setting.filters || setting.params
                    ? {
                          filter: buildFilterStr({ ...setting.filters }, setting.filterParams),
                          ...setting.params,
                      }
                    : {};

            Object.keys(paramsObj).forEach((key, index) => {
                if (index === 0) {
                    endpoint += '?';
                }
                if (index > 0) {
                    endpoint += '&';
                }
                endpoint += `${key}=${encodeURIComponent(paramsObj[key])}`;
            });

            return {
                ...setting,
                url: endpoint,
            };
        });

        return this.executeRequest$({
            method: 'post',
            url: this.generateBasicUrl('analytic.count'),
            body: settings.map((v) => v.url),
            isBackgroundRequest,
        }).pipe(
            map((result: any) => {
                return settings.map((setting) => {
                    return {
                        ...setting,
                        count: result[setting.url],
                    };
                });
            }),
            catchError((e) => {
                return of(
                    settings.map((setting) => {
                        return {
                            ...setting,
                            count: 0,
                        };
                    }),
                );
            }),
        );
    }

    /**
     * Request all ids for an entity type using `_id` endpoint.
     *
     * @param entityType - Entity type string.
     * @param settings - Request settings (params/search/urlApi).
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of ids array (strings).
     */
    allIdsRequest$(
        entityType,
        settings: {
            params: { [key: string]: string | string[] };
            search?: string;
            urlApi?: string;
        },
        isBackgroundRequest = false,
    ): Observable<string[]> {
        const paramsObj: any = {
            ...settings.params,
        };
        if (settings.search) {
            paramsObj.search = settings.search;
        }
        return this.executeRequest$({
            method: 'get',
            url: this.generateBasicUrl(entityType, '', '_id', settings.urlApi || this.apiUrl),
            params: paramsObj,
            isBackgroundRequest,
        });
    }

    /**
     * Execute a group/bulk request using `<apiUrl>/bulk/` endpoint.
     *
     * Each request item is converted to `{ endpoint, method, body }` and sent in one request.
     * When `isBackgroundRequest` is true, uses `processBackgroundBulkResponse()` for error handling.
     *
     * @param params - Array of request items (`GroupRequestItem`).
     * @param isBackgroundRequest - Background request flag. Default: false.
     * @returns Observable of processed results array (entities/lists/raw responses depending on requestType).
     */
    groupRequest$(params: GroupRequestItem[], isBackgroundRequest = false) {
        const endpoints = params.map((requestItem) => {
            let endpoint = this.generateBasicUrl(
                requestItem.entityType,
                requestItem.entityId,
                '',
                this.apiUrl,
            );
            const meta = requestItem.meta || [];
            // если есть намек на лист, то нужна в мета pagination
            if (requestItem.requestType === 'list') {
                meta.push('pagination');
            }

            const paramsObj: any = { ...requestItem.params };
            if (requestItem.offset !== undefined) {
                paramsObj['page[offset]'] = String(requestItem.offset);
            }
            if (requestItem.limit !== undefined) {
                paramsObj['page[limit]'] = String(requestItem.limit);
            }
            if (requestItem.sort !== undefined) {
                paramsObj.sort = requestItem.sort;
            }
            if (meta.length > 0) {
                paramsObj.meta = meta.join(',');
            }

            endpoint += `?${this._getLangParam()}`;

            Object.keys(paramsObj).forEach((key) => {
                endpoint += `&${key}=${encodeURIComponent(paramsObj[key])}`;
            });

            const method = requestItem.method || 'get';
            let body = method === 'PATCH' || method === 'POST' ? requestItem.body || {} : null;

            return { endpoint, method, body };
        });

        return this.executeRequest$({
            method: 'post',
            url: `${this.apiUrl}/bulk/`,
            body: endpoints,
            isBackgroundRequest,
        }).pipe(
            map((results: any) =>
                isBackgroundRequest
                    ? this.processBackgroundBulkResponse(results, params)
                    : this.processBulkResponse(results, params),
            ),
        );
    }

    /**
     * Process bulk responses for background requests.
     *
     * If any response contains errors, throws a single `HttpErrorResponse` with merged errors list.
     * Otherwise, delegates to `processBulkResponse()`.
     *
     * @param results - Raw bulk results array.
     * @param requestParams - Original request params array (optional).
     * @returns Processed results array or throws.
     */
    protected processBackgroundBulkResponse(
        results: any[],
        requestParams: GroupRequestItem[] = null,
    ) {
        const errorResponses = results.filter((v) => v.response?.errors?.length);
        if (!errorResponses.length) return this.processBulkResponse(results, requestParams);

        const error = new HttpErrorResponse({
            status: 422,
            error: {
                errors: errorResponses
                    .filter((v) => v.endpoint)
                    .map(
                        (v) =>
                            v.response?.errors?.map((error) => ({
                                ...error,
                                endpoint: v.endpoint,
                            })) || [],
                    )
                    .reduce((acc, current) => acc.concat(current), []),
            },
        });

        throw error;
    }

    /**
     * Process bulk responses and convert each response to `EntData` / `EntList` where applicable.
     *
     * When request type is `any`, returns raw `response` value.
     *
     * @param results - Raw bulk results array.
     * @param requestParams - Original request params array (optional).
     * @returns Array of processed results (same order as input).
     */
    protected processBulkResponse(results: any[], requestParams: GroupRequestItem[] = null) {
        return results.map((result, index) => {
            const response = result.response;
            if (response) {
                if (requestParams && requestParams[index].requestType === 'any') return response;

                if (response.data) {
                    let result: any | any[] = response.data;

                    result =
                        result instanceof Array
                            ? this.refineList(response, {
                                  entityType: requestParams && requestParams[index].entityType,
                              })
                            : this.makeupEntity(response.data as SrvEntData);

                    result = this.addMetaInfo(result, response.meta);
                    if (response.included) {
                        result.included = response.included.map((includedItem) =>
                            this.makeupEntity(includedItem),
                        );
                    }
                    return result;
                } else return null;
            } else return null;
        });
    }

    /**
     * Build a human-readable message from JSON:API error response.
     *
     * @param error - Error object (typically `HttpErrorResponse`).
     * @returns Concatenated error messages string.
     */
    generateErrorMessage(error) {
        let messages = [];
        error?.error?.errors
            ?.filter((e) => !!e)
            .forEach((error) => {
                let message = '';
                message += error.detail ? ` ${error.detail}` : '';
                message += error.source && error.source.pointer ? ` ${error.source.pointer}` : '';
                messages.push(message);
            });

        return messages.join('. ');
    }

    /**
     * Build a human-readable message for HTTP 409 error responses.
     *
     * Uses `error.title` from each error item.
     *
     * @param error - Error object (typically `HttpErrorResponse`).
     * @returns Concatenated titles string.
     */
    generate409ErrorMessage(error) {
        let messages = [];
        error?.error?.errors
            ?.filter((e) => !!e)
            .forEach((error) => {
                let message = error.title;
                messages.push(message);
            });
        return messages.join('. ');
    }

    /**
     * Generate a JSON:API request body for entity create/update requests.
     *
     * @param entityType - Entity type string.
     * @param entityId - Entity id string (can be null for create).
     * @param attributeData - Attributes object.
     * @param relationshipsData - Relationships object in JSON:API format. Default: {}.
     * @returns JSON:API body object `{ data: { id, type, attributes, relationships } }`.
     */
    generateEntityBody(entityType, entityId, attributeData, relationshipsData = {}) {
        return {
            data: {
                id: entityId,
                type: entityType,
                attributes: {
                    ...attributeData,
                },
                relationships: { ...relationshipsData },
            },
        };
    }

    /**
     * Poll background task result via HTTP.
     *
     * Requests `${apiUrl}/bg/task/<taskId>/` every 3 seconds and completes when:
     * - `data.attributes.result` is present, or
     * - `data.attributes.error` is present, or
     * - `data.attributes.state === 'done'`
     *
     * @param taskId - Background task id string.
     * @returns Observable that emits task result object or `{ error }` and completes.
     */
    getTaskResult$(taskId) {
        const observable = this.http.get(`${this.apiUrl}/bg/task/${taskId}/`);
        return timer(0, 3000).pipe(
            switchMap(() => observable),
            filter(
                (v: any) =>
                    !!v?.data?.attributes?.result ||
                    !!v?.data?.attributes?.error ||
                    v?.data?.attributes?.state === 'done',
            ),
            map(
                (result) =>
                    result.data.attributes?.result || { error: result?.data?.attributes?.error },
            ),
            take(1),
        );
    }

    /**
     * Execute a raw HTTP request with JSON:API headers and optional background-task support.
     *
     * When `isBackgroundRequest` is true, sets `X-Async-Background: true` header and,
     * if response contains `meta.async_background_id`, waits for async result via `getAsyncTaskResult$()`.
     *
     * @typeParam T - Expected response type.
     * @param options - Request options.
     * @param options.method - HTTP method.
     * @param options.url - Full URL.
     * @param options.body - Optional request body.
     * @param options.params - Optional query params object.
     * @param options.headers - Optional extra headers map.
     * @param options.isBackgroundRequest - Background task flag. Default: false.
     * @returns Observable of response.
     */
    executeRequest$<T>(options: {
        method: 'get' | 'post' | 'put' | 'delete' | 'patch';
        url: string;
        body?: any;
        params?: { [key: string]: string | string[] };
        headers?: { [key: string]: string };
        isBackgroundRequest?: boolean;
    }): Observable<T> {
        let { method, url, body, params, headers, isBackgroundRequest = false } = options;

        let requestHeaders = HEADERS;

        if (headers) {
            for (let key in headers) {
                requestHeaders = requestHeaders.set(key, headers[key]);
            }
        }

        if (isBackgroundRequest) {
            requestHeaders = requestHeaders.set('X-Async-Background', 'true');
        }

        const requestOptions = {
            body,
            params: new HttpParams({ fromObject: params }),
            headers: requestHeaders,
            observe: 'body' as const,
        };

        let request$: Observable<T>;

        switch (method) {
            case 'get':
                request$ = this.http.get<T>(url, requestOptions);
                break;
            case 'post':
                request$ = this.http.post<T>(url, body, requestOptions);
                break;
            case 'put':
                request$ = this.http.put<T>(url, body, requestOptions);
                break;
            case 'delete':
                request$ = this.http.delete<T>(url, requestOptions);
                break;
            case 'patch':
                request$ = this.http.patch<T>(url, body, requestOptions);
                break;
            default:
                throw new Error(`Unsupported request method: ${method}`);
        }

        if (!isBackgroundRequest) return request$;

        return request$.pipe(
            switchMap((response) => {
                const taskId = (response as SrvResponse).meta.async_background_id;
                return taskId ? this.getAsyncTaskResult$<T>(taskId) : of(response);
            }),
        );
    }

    /**
     * Wait for async background task completion using websocket and then fetch stored response.
     *
     * Listens for websocket notification `notification.async_task` with matching `taskId`.
     * When status is `completed` or `failed`, requests:
     * `${apiUrl}/async_background_response/<taskId>/` and returns `response` field.
     *
     * @typeParam T - Expected response type.
     * @param taskId - Background task id string.
     * @returns Observable of async task response, or throws formatted error when `errors` exist.
     */
    getAsyncTaskResult$<T>(taskId): Observable<T> {
        const wsService = this.injector.get(BazisWebSocketService);
        return wsService.message$.pipe(
            filter(
                (v) =>
                    v.type === 'notification.async_task' &&
                    v.id === taskId &&
                    (v?.$snapshot?.status === 'completed' || v?.$snapshot?.status === 'failed'),
            ),
            switchMap(() =>
                this.http.get<T>(`${this.apiUrl}/async_background_response/${taskId}/`),
            ),
            map((v: any) => {
                if (v.error) {
                    return { errors: [v.error] };
                }
                return v.response;
            }),
            map((response) => {
                if (response?.errors?.length) {
                    // eslint-disable-next-line @typescript-eslint/no-throw-literal
                    throw { error: { errors: response.errors } };
                }
                return response;
            }),
            take(1),
        );
    }

    /**
     * Build query param string for current active language.
     *
     * @returns String in format `lang=<activeLang>`.
     */
    protected _getLangParam() {
        return `lang=${this.translocoService.getActiveLang()}`;
    }
}
